// WP13 Use Case detail — authoritative market.useCases object.
// UPSTREAM / PUBLISHER GAPS handled honestly: no use-case weekly score
// timeline, no use-case structural brief, no geography composition in the
// active package — no substitutes are generated.
import React from "react";
import { Link, useParams } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useWp12 } from "./useWp12";
import { MarketLayout } from "./MarketTree";
import { StateGrid } from "./StateGrid";
import { RegionMiniMap } from "./RegionMiniMap";
import { zoneColor } from "./LeadersChart";
import { StateChip, patternDisplayTitle } from "./TopPatternsTable";
import { DeepInsightDot } from "./DeepInsight";
import { Sparkline } from "./charts";

const enumLabel = (v) => (typeof v === "string" ? v.replace(/_/g, " ").toLowerCase() : "—");

// Zone-hue lightness variants so multiple Pattern lines share the zone identity.
const zoneShade = (base, i) => {
    const m = /^hsl\((\d+)\s+(\d+)%\s+(\d+)%\)$/.exec(base || "");
    if (!m) return base;
    const L = [45, 60, 30, 72, 20][i % 5];
    return `hsl(${m[1]} ${m[2]}% ${L}%)`;
};

// Comparative 12-week PATTERN score histories for the leading published
// Patterns in this Use Case. Explicitly NOT a Use Case score timeline (none
// is published). Ordering = existing authoritative strength ordering.
const LeadingPatternsChart = ({ patterns, color }) => {
    const top = patterns.slice(0, 5).filter((c) => (c.scoreTimeline12w || []).length > 1);
    if (top.length === 0) return null;
    const weekSet = new Set();
    for (const c of top) for (const pt of c.scoreTimeline12w) weekSet.add(pt.analyticalWeek);
    const weeks = [...weekSet].sort();
    const rows = weeks.map((w) => {
        const row = { week: w };
        for (const c of top) {
            const pt = c.scoreTimeline12w.find((x) => x.analyticalWeek === w);
            row[c.canonicalPatternId] = pt ? pt.displaySignalScoreExact : null;
        }
        return row;
    });
    return (
        <section className="space-y-2" data-testid="wp13-usecase-leading-patterns">
            <h2 className="text-sm font-semibold">Leading patterns in this use case</h2>
            <p className="text-[10px] text-[hsl(var(--mute))]">
                Authoritative 12-week <span className="font-medium">Pattern</span> score histories of the strongest published Patterns — a weekly Use Case score timeline is not published in the active package.
            </p>
            <div className="card-flat p-4">
                <ResponsiveContainer width="100%" height={210}>
                    <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--line))" vertical={false} />
                        <XAxis dataKey="week" tick={{ fontSize: 9, fill: "hsl(var(--mute))" }}
                               tickFormatter={(w) => String(w).replace("2026-", "")} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                        <YAxis domain={["auto", 100]} allowDataOverflow tick={{ fontSize: 9, fill: "hsl(var(--mute))" }}
                               tickFormatter={(v) => Math.round(v)} tickLine={false} axisLine={false} width={36} />
                        <Tooltip
                            contentStyle={{ fontSize: 10, borderRadius: 6, border: "1px solid hsl(var(--line))" }}
                            formatter={(v, key) => {
                                const c = top.find((x) => x.canonicalPatternId === key);
                                return [v != null ? Number(v).toFixed(1) : "—", c ? patternDisplayTitle(c) : key];
                            }}
                        />
                        {top.map((c, i) => (
                            <Line key={c.canonicalPatternId} type="monotone" dataKey={c.canonicalPatternId}
                                  stroke={zoneShade(color, i)} strokeWidth={1.6} dot={false} connectNulls={false} isAnimationActive={false} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {top.map((c, i) => (
                        <div key={c.canonicalPatternId} className="flex items-center gap-2 min-w-0">
                            <span className="w-3 h-0.5 rounded flex-shrink-0" style={{ background: zoneShade(color, i) }} />
                            <Link to={`/market-v2/patterns/${encodeURIComponent(c.canonicalPatternId)}`}
                                  data-testid={`wp13-usecase-leader-${c.canonicalPatternId}`}
                                  className="text-[10px] truncate hover:text-[hsl(var(--accent-ink))] hover:underline">
                                {patternDisplayTitle(c)}
                            </Link>
                            <span className="num text-[9px] text-[hsl(var(--mute))] flex-shrink-0">{c.strength?.displayScoreExact?.toFixed(1)}</span>
                        </div>
                    ))}
                </div>
                <p className="text-[9px] text-[hsl(var(--mute))] mt-2">
                    These are Pattern score timelines, not a Use Case score timeline. Ordering follows the existing authoritative Pattern strength ordering — no new ranking.
                </p>
            </div>
        </section>
    );
};

export default function UseCaseDetail() {
    const { useCaseKey } = useParams();
    const { status, data: market } = useWp12("market");

    if (status === "loading") return <div className="p-8 text-sm text-[hsl(var(--mute))]">Loading…</div>;
    const uc = (market?.useCases || []).find((u) => u.scopeKey === useCaseKey);
    if (!uc) {
        return (
            <div className="p-8 space-y-2" data-testid="wp13-usecase-notfound">
                <p className="text-sm">Use Case not found in the active publication package.</p>
                <Link to="/market-v2/market" className="text-xs text-[hsl(var(--accent-ink))] hover:underline">← Back to Market</Link>
            </div>
        );
    }

    const zone = (market.marketZones || []).find((z) => (uc.marketZoneKeys || []).includes(z.scopeKey));
    const color = zoneColor(zone?.presentationColorToken);
    const h4 = (uc.horizons || []).find((h) => h.horizonWeeks === 4);
    const h12 = (uc.horizons || []).find((h) => h.horizonWeeks === 12);
    const refs = new Set(uc.patternRefs || []);
    const patterns = (market.patternCards || [])
        .filter((c) => refs.has(c.canonicalPatternId))
        .sort((a, b) => (b.strength?.displayScoreExact ?? -1) - (a.strength?.displayScoreExact ?? -1));

    return (
        <MarketLayout market={market} selectedUseCaseKey={useCaseKey}>
            <div className="space-y-8" data-testid="wp13-usecase-detail">
                {/* Header — object type distinguished from object name */}
                <div className="border-l-4 pl-4" style={{ borderColor: color }}>
                    <div className="eyebrow" data-testid="wp13-usecase-eyebrow">
                        {zone ? <Link to={`/market-v2/market?zone=${encodeURIComponent(zone.scopeKey)}`} className="hover:underline">{zone.label}</Link> : "Market"} · Use Case
                    </div>
                    <h1 className="text-xl font-semibold mt-0.5" data-testid="wp13-usecase-title">{uc.label}</h1>
                    <div className="flex items-baseline gap-5 flex-wrap mt-2">
                        <span data-testid="wp13-usecase-score">
                            <span className="num text-xl font-semibold">{uc.strength?.displayScoreExact != null ? uc.strength.displayScoreExact.toFixed(1) : "—"}</span>
                            <span className="text-[10px] text-[hsl(var(--mute))] ml-1.5">{enumLabel(uc.strength?.scoreBand)}</span>
                        </span>
                        <span className="num text-[10px] text-[hsl(var(--mute))]">{uc.counts?.patternCount ?? "—"} published patterns</span>
                    </div>
                    <div className="mt-3">
                        <StateGrid testId="wp13-usecase-state" items={[
                            { label: "4w trend", value: h4?.trend?.primaryTrendClass },
                            { label: "12w trend", value: h12?.trend?.primaryTrendClass },
                            { label: "Velocity", value: h12?.trend?.velocityState },
                            { label: "Breadth", value: h12?.breadth?.actorTrendClass },
                            { label: "Persistence", value: h12?.trend?.persistenceState },
                            { label: "Reliability", value: h12?.trend?.reliability, plain: true },
                            { label: "Claim eligibility", value: h12?.trend?.claimEligibility, plain: true },
                        ]} />
                    </div>
                </div>

                {/* Comparative Pattern timelines — explicitly NOT a Use Case timeline */}
                <LeadingPatternsChart patterns={patterns} color={color} />

                {/* Geography — associated regions only where published */}
                {(uc.geographyRefs || []).length > 0 && (
                    <section className="space-y-2" data-testid="wp13-usecase-geography">
                        <h2 className="text-sm font-semibold">Region-linked observed activity</h2>
                        <div className="card-flat p-3 max-w-xl"><RegionMiniMap geoKeys={uc.geographyRefs} color={color} /></div>
                    </section>
                )}

                {/* Pattern comparison */}
                <section className="space-y-2" data-testid="wp13-usecase-patterns">
                    <h2 className="text-sm font-semibold">Patterns in this use case</h2>
                    <div className="card-flat overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] border-b border-[hsl(var(--line))]">
                                    <th className="px-4 py-2">Pattern</th>
                                    <th className="px-2 py-2 text-right">Score</th>
                                    <th className="px-2 py-2">4w</th>
                                    <th className="px-2 py-2">12w</th>
                                    <th className="px-2 py-2">Velocity</th>
                                    <th className="px-2 py-2">Breadth</th>
                                    <th className="px-2 py-2 pr-4">12w trajectory</th>
                                </tr>
                            </thead>
                            <tbody>
                                {patterns.map((c) => {
                                    const t4 = c.horizonSummary?.h4?.trend, t12 = c.horizonSummary?.h12;
                                    return (
                                        <tr key={c.canonicalPatternId} className="border-b border-[hsl(var(--line))] last:border-0 hover:bg-[hsl(var(--surface-sunken))]">
                                            <td className="px-4 py-2 max-w-[280px]">
                                                <div className="flex items-center gap-1.5 min-w-0">
                                                    <Link to={`/market-v2/patterns/${encodeURIComponent(c.canonicalPatternId)}`}
                                                          className="text-[11px] font-medium truncate block hover:text-[hsl(var(--accent-ink))] hover:underline">
                                                        {patternDisplayTitle(c)}
                                                    </Link>
                                                    {c.hasDurableInterpretation && <DeepInsightDot testId={`wp13-di-${c.canonicalPatternId}`} />}
                                                </div>
                                                {c.displayTitle && <div className="text-[8.5px] text-[hsl(var(--mute))] truncate">{c.patternLabel}</div>}
                                            </td>
                                            <td className="px-2 py-2 text-right whitespace-nowrap">
                                                <span className="num text-[10.5px] font-medium">{c.strength?.displayScoreExact?.toFixed(1) ?? "—"}</span>
                                                <span className="text-[8.5px] text-[hsl(var(--mute))] ml-1">{enumLabel(c.strength?.scoreBand)}</span>
                                            </td>
                                            <td className="px-2 py-2"><StateChip value={t4?.primaryTrendClass} /></td>
                                            <td className="px-2 py-2"><StateChip value={t12?.trend?.primaryTrendClass} /></td>
                                            <td className="px-2 py-2"><StateChip value={t12?.trend?.velocityState} /></td>
                                            <td className="px-2 py-2"><StateChip value={t12?.breadth?.actorTrendClass} /></td>
                                            <td className="px-2 py-2 pr-4">
                                                {(c.scoreTimeline12w || []).length > 1 && (
                                                    <Sparkline values={c.scoreTimeline12w.map((p) => p.displaySignalScoreExact)} width={80} height={20} />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-[9px] text-[hsl(var(--mute))]">Pattern scores are global authoritative pattern-scoped scores — not recalculated per use case.</p>
                </section>
            </div>
        </MarketLayout>
    );
}
